import * as React from "react"
import { useRouter } from "next/router"

import { Center, Spinner } from "@chakra-ui/react"

import { REDIRECT_PATH } from "lib/config"
import { MeContext, useMeQuery } from "lib/hooks/useMe"

// Pages that render without an authenticated admin. Values are Next.js route
// patterns (router.pathname), not concrete URLs.
const PUBLIC_ROUTES = ["/login", "/reset-password/[token]"]

const FullPageSpinner = () => (
  <Center minH="100vh">
    <Spinner />
  </Center>
)

/**
 * App-level authentication gate and session provider. Public routes render
 * ungated (and bounce an already-authenticated admin away); every other route
 * waits for the session, redirects to /login on an unauthenticated result, and
 * exposes the resolved admin to the tree via {@link MeContext} so `useMe()`
 * always returns a non-null admin. Per-page permission checks stay in withAuth.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter()
  const { me, isPending } = useMeQuery()
  const isPublicRoute = PUBLIC_ROUTES.includes(router.pathname)
  const redirect = router.query[REDIRECT_PATH] as string | undefined

  React.useEffect(() => {
    if (isPending) return
    if (isPublicRoute) {
      // Already signed in — skip login/reset and go where they were headed.
      if (me) router.replace(redirect || "/")
      return
    }
    if (!me) {
      router.replace(
        router.asPath === "/"
          ? "/login"
          : `/login?${REDIRECT_PATH}=${router.asPath}`,
      )
    }
  }, [isPending, me, isPublicRoute, redirect, router])

  if (isPublicRoute) {
    // Hold the spinner while a signed-in admin is being redirected away.
    if (isPending || me) return <FullPageSpinner />
    return <>{children}</>
  }

  if (isPending) return <FullPageSpinner />
  if (!me) return <></>
  return <MeContext.Provider value={me}>{children}</MeContext.Provider>
}
